import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Gutter,
  Grid,
  GridColumn,
  Layout,
  useApi,
  useApiWithErrors,
  ApiError,
} from '@axiom/ui';
import type { CandidateWorkFeedItem } from '@axiom/validation';

import { UserApi } from '../../api/user-api';
import { CandidateOpportunitiesApi } from '../../api/candidate-opportunities-api';
import { CandidateApi } from '../../api/candidate-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { WORK_FEED_VIEWS } from '../../utils/WorkFeedUtil';
import { WorkFeedCard } from '../WorkFeedCard/WorkFeedCard';
import { OpportunityPhaseTracker } from '../OpportunityPhaseTracker/OpportunityPhaseTracker';
import { WorkFeedInterestedTracker } from '../WorkFeedInterestedTracker/WorkFeedInterestedTracker';

import { WorkFeedSPVBackButton } from './WorkFeedSPVBackButton';
import { WorkFeedSPVFooter } from './WorkFeedSPVFooter';
import { WorkFeedSPVEmpty } from './WorkFeedSPVEmpty';

export const WorkFeedSPV = () => {
  let shouldShowEmptyState = false;
  let workFeedItem: CandidateWorkFeedItem | null = null;

  const navigate = useNavigate();
  const { opportunityId, candidateId: urlCandidateId } = useParams();
  if (!opportunityId) {
    throw new Error('No opportunityId provided');
  }
  const [{ data: user }] = useApi(UserApi.readUser());

  const candidateId = CandidateUtil.reconcileUserCandidateId(
    user,
    urlCandidateId
  );
  const [{ data: candidate }, { data: profileIssues }] = useApi(
    CandidateApi.readCandidate(candidateId),
    CandidateProfileIssuesApi.readCandidateProfileIssues(candidateId)
  );
  const [workFeedItemResponse] = useApiWithErrors(
    CandidateOpportunitiesApi.readCandidateWorkFeedItem({
      candidateId,
      opportunityId,
    })
  );

  useEffect(() => {
    if (workFeedItemResponse instanceof ApiError) {
      const error = workFeedItemResponse as ApiError;
      if (error.getStatusCode() === 403) {
        const failUrl = `../403`;
        void navigate(failUrl, { replace: true });
      }
    } else if (!profileIssues.isSubmissionReady) {
      const actionNeededUrl = '../actionNeeded';
      void navigate(actionNeededUrl, { replace: true });
    }
  }, [workFeedItemResponse, profileIssues.isSubmissionReady]);

  if (workFeedItemResponse instanceof ApiError) {
    const error = workFeedItemResponse as ApiError;
    if (error.getStatusCode() === 403) {
      return null;
    } else if (error.getStatusCode() === 423) {
      shouldShowEmptyState = true;
    } else {
      throw error;
    }
  } else {
    workFeedItem = workFeedItemResponse.data;
  }

  return (
    <Grid>
      <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
        <WorkFeedInterestedTracker
          candidateId={candidateId}
          name="WORK_FEED_INTERESTED_TRACKER"
        />
        <Gutter bottom="16px" />
      </GridColumn>
      <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
        <Layout direction="vertical" verticalGutter="16px">
          <WorkFeedSPVBackButton />
          {shouldShowEmptyState && <WorkFeedSPVEmpty />}
          {!shouldShowEmptyState && !!workFeedItem && (
            <>
              <WorkFeedCard
                workFeedItem={workFeedItem}
                candidate={candidate}
                view={WORK_FEED_VIEWS.spv}
                index={0}
                name="WORK_FEED_CARD"
              />
              <OpportunityPhaseTracker
                workFeedItem={workFeedItem}
                name="OPPORTUNITY_PHASE_TRACKER"
              />
              <WorkFeedSPVFooter candidateId={candidate.id} />
            </>
          )}
        </Layout>
      </GridColumn>
    </Grid>
  );
};
